<template>
  <FormulateForm
    ref="policyForm"
    name="policyForm"
  >
    <b-card-actions
      :title="$t('Dados da política')"
      no-actions
      :show-loading="saving"
    >
      <b-row>
        <b-col md="5">
          <FormulateInput
            id="policy-name"
            v-model="formPolicy.name"
            :label="$t('Nome')"
            name="name"
            type="text"
            class="required"
            validation="required"
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            id="policy-cost-config"
            v-model="formPolicy.acquirerCostConfigId"
            :label="$t('Configuração de custo')"
            name="acquirerCostConfigId"
            type="vue-select"
            class="required"
            validation="required"
            :placeholder="$t('Selecione')"
            :options="costConfigOptions"
            :instruction="
              $t('Determina o custo da adquirente usado para apurar a cobertura desta política')
            "
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="policy-priority"
            v-model.number="formPolicy.priority"
            :label="$t('Prioridade')"
            name="priority"
            type="text-number"
            :instruction="
              $t('Na resolução, a política de maior prioridade vence. Empate desempata pelo escopo mais específico.')
            "
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <FormulateInput
            id="policy-stores"
            v-model="formPolicy.storeIds"
            :label="$t('Lojas')"
            name="storeIds"
            type="vue-select"
            multiple
            :placeholder="$t('Todas as lojas')"
            :options="storeOptions"
            :instruction="
              $t('Deixe vazio para alcançar todas as lojas. Uma política pode reger várias lojas.')
            "
          />
        </b-col>
        <b-col md="6">
          <FormulateInput
            id="policy-price-tables"
            v-model="formPolicy.priceTableIds"
            :label="$t('Tabelas de preço')"
            name="priceTableIds"
            type="vue-select"
            multiple
            :placeholder="$t('Todas as tabelas')"
            :options="getComboPriceTables"
            :instruction="$t('Deixe vazio para alcançar todas as tabelas de preço')"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <p class="text-muted mb-0">
            <feather-icon icon="InfoIcon" />
            {{ scopeSummary }}
          </p>
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card-actions
      :title="$t('Vigência e limites')"
      no-actions
      :show-loading="saving"
    >
      <b-row>
        <b-col md="3">
          <FormulateInput
            id="policy-effective-from"
            v-model="formPolicy.effectiveFrom"
            :label="$t('Vigente de')"
            name="effectiveFrom"
            type="datepicker"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="policy-effective-to"
            v-model="formPolicy.effectiveTo"
            :label="$t('Vigente até')"
            name="effectiveTo"
            type="datepicker"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="policy-max-installments"
            v-model.number="formPolicy.maxInstallments"
            :label="$t('Máximo de parcelas')"
            name="maxInstallments"
            type="text-number"
            class="required"
            validation="required|min:1|max:24"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="policy-max-without-surcharge"
            v-model.number="formPolicy.maxInstallmentsWithoutSurcharge"
            :label="$t('Máx. parcelas sem acréscimo')"
            name="maxInstallmentsWithoutSurcharge"
            type="text-number"
            class="required"
            validation="required|min:1|max:24"
            :instruction="$t('Acima desta quantidade, o acréscimo cadastrado passa a valer')"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col md="3">
          <FormulateInput
            id="policy-min-installment-value"
            v-model.number="formPolicy.minInstallmentValue"
            :label="$t('Valor mínimo da parcela')"
            name="minInstallmentValue"
            type="text-number"
            currency="R$"
            :precision="2"
            class="required"
            validation="required"
            :instruction="$t('Zero significa sem restrição')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="policy-min-sale-value"
            v-model.number="formPolicy.minSaleValue"
            :label="$t('Valor mínimo da venda')"
            name="minSaleValue"
            type="text-number"
            currency="R$"
            :precision="2"
            class="required"
            validation="required"
            :instruction="
              $t('Abaixo deste valor a venda só é oferecida à vista. Zero significa sem restrição.')
            "
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="policy-round-installment"
            v-model="formPolicy.roundInstallment"
            :label="$t('Parcela a arredondar')"
            name="roundInstallment"
            type="vue-select"
            class="required"
            validation="required"
            :clearable="false"
            :options="roundInstallmentOptions()"
            :instruction="
              $t('Onde cai a diferença de centavos do rateio, para a soma fechar com o total')
            "
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="policy-default-mode"
            v-model="formPolicy.defaultMode"
            :label="$t('Modalidade padrão')"
            name="defaultMode"
            type="vue-select"
            class="required"
            validation="required"
            :placeholder="$t('Selecione')"
            :options="installmentModeOptions"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <FormulateInput
            id="policy-allowed-modes"
            v-model="formPolicy.allowedModes"
            :label="$t('Modalidades permitidas')"
            name="allowedModes"
            type="vue-select"
            multiple
            class="required"
            validation="required"
            :placeholder="$t('Selecione')"
            :options="installmentModeOptions"
            :instruction="$t('A modalidade padrão precisa estar entre as permitidas')"
          />
        </b-col>
        <b-col md="6">
          <FormulateInput
            id="policy-apply-surcharge"
            v-model="formPolicy.applySurchargeToCustomer"
            :label="$t('Repassar acréscimo ao cliente')"
            name="applySurchargeToCustomer"
            type="switch"
            :instruction="
              $t(
                'Desligado, a Loja absorve o custo e os acréscimos cadastrados servem apenas como referência de cobertura'
              )
            "
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card-actions
      :title="$t('Acréscimo por quantidade de parcelas')"
      no-actions
      :show-loading="saving"
    >
      <b-table
        show-empty
        responsive
        striped
        small
        class="bordered"
        :empty-text="$t('Nenhum acréscimo cadastrado')"
        :fields="surchargeFields"
        :items="formPolicy.surcharges"
      >
        <template #cell(surchargePercent)="row">
          {{ formatPercent(row.item.surchargePercent) }}
        </template>
        <template #cell(action)="row">
          <e-grid-actions
            :show-update="false"
            :show-delete="true"
            @delete="removeSurcharge(row.item)"
          />
        </template>
      </b-table>

      <b-row class="mt-1">
        <b-col md="3">
          <FormulateInput
            id="new-surcharge-installments"
            v-model.number="newSurcharge.installments"
            :label="$t('Parcelas')"
            type="text-number"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="new-surcharge-percent"
            v-model="newSurcharge.surchargePercent"
            :label="$t('Acréscimo (%)')"
            type="text-number"
            :precision="4"
            suffix=" %"
          />
        </b-col>
        <b-col
          md="3"
          class="d-flex align-items-end pb-1"
        >
          <e-button
            :text="$t('Adicionar')"
            variant="primary"
            @click="addSurcharge"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <installment-policy-coverage
      v-if="isEdition"
      :policy-id="formPolicy.id"
      :apply-surcharge="formPolicy.applySurchargeToCustomer"
    />

    <e-crud-actions
      :busy="saving"
      @save="savePolicy"
      @cancel="cancel"
    />
  </FormulateForm>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { BRow, BCol, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import axiosInstance from '@/libs/axios'
import { cardDomain } from '@/mixins'
import EButton from '@/views/components/EButton.vue'
import ECrudActions from '@/views/components/ECrudActions.vue'
import EGridActions from '@/views/components/EGridActions.vue'
import InstallmentPolicyCoverage from './InstallmentPolicyCoverage.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BCardActions,
    EButton,
    ECrudActions,
    EGridActions,
    InstallmentPolicyCoverage,
  },

  mixins: [cardDomain],

  data() {
    return {
      saving: false,
      costConfigOptions: [],
      newSurcharge: { installments: null, surchargePercent: null },
    }
  },

  computed: {
    ...mapState(
      'pages/settings/financial/installmentPolicy/installmentPolicyMaintain',
      ['formPolicy']
    ),
    ...mapGetters('app', ['storeOptions']),
    ...mapGetters('common/priceTables', ['getComboPriceTables']),

    isEdition() {
      return !!this.$route.params.id
    },

    installmentModeOptions() {
      return [
        { value: 'Cash', label: this.$t('À vista') },
        { value: 'Store', label: this.$t('Parcelado lojista (sem juros)') },
        { value: 'Issuer', label: this.$t('Parcelado administradora (com juros)') },
      ]
    },

    /**
     * Explicita a semântica do escopo vazio. É a parte do cadastro mais fácil de entender ao
     * contrário: deixar em branco não desabilita a política, alcança tudo.
     */
    scopeSummary() {
      const stores = this.formPolicy.storeIds?.length
        ? `${this.formPolicy.storeIds.length} ${this.$t('loja(s) selecionada(s)')}`
        : this.$t('todas as lojas')

      const tables = this.formPolicy.priceTableIds?.length
        ? `${this.formPolicy.priceTableIds.length} ${this.$t('tabela(s) selecionada(s)')}`
        : this.$t('todas as tabelas de preço')

      return `${this.$t('Esta política alcança')} ${stores} ${this.$t('e')} ${tables}.`
    },


    surchargeFields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Parcelas'),
          key: 'installments',
          class: 'text-center',
          thStyle: { width: '150px' },
        },
        {
          label: this.$t('Acréscimo'),
          key: 'surchargePercent',
          class: 'text-right',
        },
      ]
    },
  },

  async mounted() {
    await this.fetchCostConfigOptions()

    if (this.isEdition) {
      try {
        this.saving = true
        await this.stFetchById(this.$route.params.id)
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    }
  },

  methods: {
    ...mapActions(
      'pages/settings/financial/installmentPolicy/installmentPolicyMaintain',
      {
        stFetchById: 'fetchById',
        stSaveForm: 'saveForm',
        stSetSurcharges: 'setSurcharges',
        stCleanState: 'cleanState',
      }
    ),

    formatPercent(value) {
      if (value === null || value === undefined) return ''
      return `${Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}%`
    },

    async fetchCostConfigOptions() {
      try {
        const { data } = await axiosInstance.get(
          '/api/settings/financial/acquirer-cost-configs',
          { params: { active: true, pageSize: 0, pageIndex: 0 } }
        )

        this.costConfigOptions = (data.results || []).map(config => ({
          value: config.id,
          label: config.name,
        }))
      } catch (error) {
        this.showError({ error })
      }
    },

    addSurcharge() {
      const { installments, surchargePercent } = this.newSurcharge

      if (!installments || surchargePercent === null || surchargePercent === undefined) {
        this.showInvalidDataMessage()
        return
      }

      // Uma quantidade de parcelas não pode ter dois acréscimos: a API rejeita, e aqui a
      // substituição é mais útil que o erro.
      const surcharges = (this.formPolicy.surcharges || []).filter(
        item => item.installments !== installments
      )

      surcharges.push({ installments, surchargePercent })
      surcharges.sort((a, b) => a.installments - b.installments)

      this.stSetSurcharges(surcharges)
      this.newSurcharge = { installments: null, surchargePercent: null }
    },

    removeSurcharge(item) {
      this.stSetSurcharges(
        (this.formPolicy.surcharges || []).filter(s => s.installments !== item.installments)
      )
    },

    /**
     * O backend responde 409 quando o cadastro é válido mas provavelmente incompleto — por exemplo,
     * faixas de parcelas acima do limite sem acréscimo, numa política que repassa. Nesse caso a
     * tela pergunta e reenvia confirmando, em vez de tratar como erro.
     */
    async savePolicy() {
      this.$refs.policyForm.showErrors()
      if (this.$refs.policyForm.hasErrors) {
        this.showInvalidDataMessage()
        return
      }

      try {
        this.saving = true

        let result = await this.stSaveForm({ confirmWarnings: false })

        if (!result.saved && result.warnings?.length) {
          const confirmed = await this.confirm({
            title: this.$t('Confirma o cadastro?'),
            text: this.buildWarningText(result.warnings),
          })

          if (!confirmed) return

          result = await this.stSaveForm({ confirmWarnings: true })
        }

        if (result.saved) {
          this.showSuccess({ message: this.$t('Política salva com sucesso') })
          this.$router.push({ name: 'installment-policy-list' })
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    buildWarningText(warnings) {
      return warnings
        .map(warning => {
          const installments = (warning.installments || []).join(', ')
          return this.$t(
            'Não há acréscimo cadastrado para as parcelas: {installments}. A Loja absorverá o custo nessas faixas.',
            { installments }
          )
        })
        .join('\n')
    },

    cancel() {
      this.stCleanState()
      this.$router.push({ name: 'installment-policy-list' })
    },
  },
}
</script>

<style lang="scss" scoped></style>
